import { getColSpanClass } from '@/utils/layout';
import { AnalyticsFilters } from '@superfunnel/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { useState } from 'react';
import { AnalyticsConfig } from '../../types/overview';
import { ChartCard } from '../charts';
import ChartRenderer from './ChartRenderer';

interface ChartsGridProps {
  /**
   * Chart configuration array from the overview config context
   */
  config: AnalyticsConfig;
  /**
   * Analytics filters to apply to all charts
   */
  analyticsFilters: AnalyticsFilters;
  /**
   * Optional campaign ID to filter analytics data
   */
  campaignId?: string;
  /**
   * Optional function to translate segment labels
   */
  translateSegmentLabel?: (
    segment: string | undefined,
    value: string,
  ) => string;
}

/**
 * Renders a responsive grid of analytics charts based on configuration.
 * Handles special rendering for trend charts with metric selectors.
 */
export default function ChartsGrid({
  config,
  analyticsFilters,
  campaignId,
  translateSegmentLabel,
}: ChartsGridProps) {
  const [selectedTrendMetric, setSelectedTrendMetric] = useState('visitors');

  return (
    <div className="grid grid-cols-12 gap-6">
      {config.map((chart) => {
        // Determine height based on chart kind
        const getChartHeight = () => {
          if (chart.kind === 'metric') return 'h-28';
          if (chart.kind === 'pie') return 'h-48';
          if (chart.kind === 'bar-horizontal') return 'h-68';
          return 'h-80';
        };

        const colClasses = getColSpanClass(chart.layout);

        // Handle trend chart specially with metric selector
        if (chart.id === 'trend_main') {
          const trendChartWithSelectedMetric = {
            ...chart,
            metrics: [selectedTrendMetric],
          };

          const metricSelector = (
            <Select
              value={selectedTrendMetric}
              onValueChange={setSelectedTrendMetric}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select metric" />
              </SelectTrigger>
              <SelectContent>
                {chart.metrics.map((metric) => (
                  <SelectItem key={metric} value={metric}>
                    {formatMetricLabel(metric)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );

          return (
            <div key={chart.id} className={colClasses}>
              <ChartCard
                title={chart.title}
                actions={metricSelector}
                className={getChartHeight()}
              >
                <ChartRenderer
                  withoutCard
                  definition={trendChartWithSelectedMetric}
                  analyticsFilters={analyticsFilters}
                  campaignId={campaignId}
                  translateSegmentLabel={translateSegmentLabel}
                />
              </ChartCard>
            </div>
          );
        }

        return (
          <div key={chart.id} className={colClasses}>
            <ChartRenderer
              definition={chart}
              analyticsFilters={analyticsFilters}
              chartClassName={getChartHeight()}
              campaignId={campaignId}
              translateSegmentLabel={translateSegmentLabel}
            />
          </div>
        );
      })}
    </div>
  );
}

function formatMetricLabel(metric: string): string {
  const labels: Record<string, string> = {
    visitors: 'Visitors',
    conversions: 'Conversions',
    cvr: 'Conversion Rate',
    leads: 'Leads',
    ad_spend: 'Ad Spend',
  };
  return labels[metric] || metric;
}
